import * as turf from '@turf/turf';
import type { Feature, FeatureCollection } from 'geojson';
import { isCircleFeature } from './map-geometry.utils';
import { pointAreaHa } from './map-point-editor.component';

export interface LatLng {
  lat: number;
  lng: number;
}

export function markPolygonShapes(polygonFeatures: Feature[]): void {
  for (const f of polygonFeatures) {
    const circle = isCircleFeature(f);
    (f.properties as any) = { ...(f.properties ?? {}), _circle: circle, shape: circle ? 'circle' : 'polygon' };
  }
}

export function siteCenter(siteFeatures: Feature[], fallback: LatLng): LatLng {
  if (!siteFeatures.length) return fallback;
  try {
    const centroid = turf.centroid({ type: 'FeatureCollection', features: siteFeatures } as any);
    const [lng, lat] = (centroid as any)?.geometry?.coordinates ?? [];
    if (Number.isFinite(lng) && Number.isFinite(lat)) return { lat, lng };
  } catch {}
  return fallback;
}

export function buildSaveResult(input: {
  drawGeoJson: FeatureCollection;
  markerPoint: LatLng;
  points: Feature[];
  polygonRings: [number, number][][];
  polygonAreasM2: number[];
}) {
  const features = input.drawGeoJson.features || [];
  const polygonFeatures = features.filter((f) => f?.geometry?.type === 'Polygon');
  const siteFeatures = features.filter((f) => f?.geometry?.type === 'Polygon' || f?.geometry?.type === 'Point');
  markPolygonShapes(polygonFeatures);
  const areaM2 = totalAreaM2(polygonFeatures, input.points);
  return {
    center: siteCenter(siteFeatures, input.markerPoint),
    point: input.markerPoint,
    polygons: input.polygonRings,
    polygonAreasM2: input.polygonAreasM2,
    totalAreaM2: areaM2,
    totalAreaHa: Math.round((areaM2 / 10000) * 100) / 100,
  };
}

export function totalAreaM2(polygonFeatures: Feature[], points: Feature[]): number {
  return (
    points.reduce((sum, point) => sum + pointAreaHa(point.properties) * 10000, 0) +
    polygonFeatures.reduce((sum, f) => {
      try {
        return sum + (turf.area(f as any) || 0);
      } catch {
        return sum;
      }
    }, 0)
  );
}
